import React from "react";

export const ACCENT = "#A78BFA";
export const SEV_COLOR = {
  "No stroke symptoms": "#4ADE80",
  "Minor stroke":       "#FBBF24",
  "Moderate stroke":    "#FB923C",
  "Moderate-severe stroke": "#F87171",
  "Severe stroke":      "#DC2626",
  "Mild":     "#4ADE80",
  "Moderate": "#FB923C",
  "Severe":   "#F87171",
  "Status migrainosus": "#DC2626",
};
export const CONTROL_COLOR = {
  "Seizure-free (≥1 yr)": "#4ADE80",
  "Well controlled":       "#4ADE80",
  "Partially controlled":  "#FBBF24",
  "Uncontrolled":          "#F87171",
};

export const NIHSS_ITEM_DEFS = [
  ["loc",             "Level of consciousness", 3,
    ["0 = Alert", "1 = Drowsy but arousable", "2 = Obtunded", "3 = Coma / unresponsive"]],
  ["loc_questions",   "LOC questions (month/age)", 2,
    ["0 = Both correct", "1 = One correct", "2 = Neither"]],
  ["loc_commands",    "LOC commands", 2,
    ["0 = Both correct", "1 = One correct", "2 = Neither"]],
  ["best_gaze",       "Best gaze", 2,
    ["0 = Normal", "1 = Partial gaze palsy", "2 = Forced deviation"]],
  ["visual",          "Visual fields", 3,
    ["0 = No loss", "1 = Partial hemianopia", "2 = Complete hemianopia", "3 = Bilateral"]],
  ["facial_palsy",    "Facial palsy", 3,
    ["0 = Normal", "1 = Minor", "2 = Partial", "3 = Complete"]],
  ["motor_arm_left",  "Motor arm — left", 4,
    ["0 = No drift", "1 = Drift", "2 = Some effort against gravity", "3 = No effort against gravity", "4 = No movement"]],
  ["motor_arm_right", "Motor arm — right", 4,
    ["0 = No drift", "1 = Drift", "2 = Some effort against gravity", "3 = No effort against gravity", "4 = No movement"]],
  ["motor_leg_left",  "Motor leg — left", 4,
    ["0 = No drift", "1 = Drift", "2 = Some effort against gravity", "3 = No effort against gravity", "4 = No movement"]],
  ["motor_leg_right", "Motor leg — right", 4,
    ["0 = No drift", "1 = Drift", "2 = Some effort against gravity", "3 = No effort against gravity", "4 = No movement"]],
  ["limb_ataxia",     "Limb ataxia", 2,
    ["0 = Absent", "1 = One limb", "2 = Two limbs"]],
  ["sensory",         "Sensory", 2,
    ["0 = Normal", "1 = Mild loss", "2 = Severe / total loss"]],
  ["language",        "Best language / aphasia", 3,
    ["0 = No aphasia", "1 = Mild-moderate", "2 = Severe", "3 = Mute / global"]],
  ["dysarthria",      "Dysarthria", 2,
    ["0 = Normal", "1 = Mild-moderate", "2 = Severe / unintelligible"]],
  ["extinction",      "Extinction / inattention", 2,
    ["0 = No abnormality", "1 = One modality", "2 = Both modalities"]],
];

export const MIGRAINE_TYPES = [
  "migraine_without_aura", "migraine_with_aura", "chronic_migraine",
  "menstrual_migraine", "hemiplegic_migraine", "vestibular_migraine",
  "tension_type", "cluster", "medication_overuse",
];
export const SEIZURE_TYPES = [
  "focal_aware", "focal_impaired_awareness", "focal_to_bilateral_tonic_clonic",
  "generalized_tonic_clonic", "generalized_absence", "generalized_myoclonic",
  "generalized_atonic", "unknown_onset", "non_epileptic",
];
export const MIGRAINE_TRIGGER_LIST = [
  "stress", "sleep_deprivation", "menstruation", "dehydration", "skipped_meal",
  "bright_light", "loud_noise", "screen_time", "weather_change", "alcohol",
  "caffeine", "chocolate", "aged_cheese", "MSG", "exercise",
];
export const SEIZURE_TRIGGER_LIST = [
  "missed_medication", "sleep_deprivation", "stress", "alcohol",
  "flashing_lights", "fever", "menstruation", "dehydration", "illness",
];

export function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}
export function humanize(v) {
  return String(v || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7] mb-1">{label}</div>
      {children}
    </label>
  );
}

export function SummaryCard({ title, value, icon: Icon }) {
  return (
    <div className="ros-card">
      <div className="flex items-center gap-2 ros-label"><Icon size={12} /> {title}</div>
      <div className="mt-2 text-3xl font-heading font-bold text-white">{value}</div>
    </div>
  );
}

export function SeverityBadge({ value }) {
  const c = SEV_COLOR[value] || "#A3ACA7";
  return (
    <span className="mt-2 inline-block text-[10px] font-bold px-2.5 py-1 rounded-full"
          data-testid="severity-badge"
          style={{ color: c, background: `${c}18`, border: `1px solid ${c}40` }}>
      {(value || "unknown").toUpperCase()}
    </span>
  );
}

export function EmptyMini({ text }) {
  return <div className="mt-4 text-sm text-[#A3ACA7]">{text}</div>;
}

export function HistoryTable({ title, testId, rows, rowTestId, empty, columns }) {
  return (
    <div className="ros-card" data-testid={testId}>
      <div className="font-heading text-lg mb-3">{title}</div>
      {rows.length === 0 ? (
        <div className="text-sm text-[#A3ACA7]">{empty}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] text-[#A3ACA7] uppercase tracking-wider">
                {columns.map((c) => <th key={c.label} className="py-2 pr-4">{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[#272E2B]" data-testid={rowTestId(r)}>
                  {columns.map((c) => (
                    <td key={c.label} className="py-2 pr-4 text-white">{c.render(r)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
